import Openings from "@Tmp/Openings";
import clsx from "clsx";
import * as React from "react";
import { FindDealerListItemProps, FindDealerListProps, FindDealerHandleSeeDealerOnMap } from ".";
import { SkeletonLoading } from "..";
import DealerIcons from "./DealerIcons";

export default function List({
    dealers,
    activeDealer,
    setActiveDealer,
    selectedDealer,
    setSelectedDealer,
    setListOpen,
    setMapOptions,
    loadingDealers,
    ...props
}: FindDealerListProps) {
    const handleSeeDealerOnMap: FindDealerHandleSeeDealerOnMap = (dealer) => {
        setMapOptions((prev) => {
            return {
                ...prev,
                center: dealer.address.coordinates,
                zoom: 9,
            };
        });

        setActiveDealer(dealer.id);
        setListOpen(false);
    };

    return (
        <div className={"tmp-find-dealer__list tmp-find-dealer__list__" + props?.id}>
            {loadingDealers
                ? [...new Array(5)].map((value, index) => (
                      <ListItem
                          settings={props?.settings}
                          key={index}
                          loadingDealers={loadingDealers}
                          activeDealer={activeDealer}
                          handleSeeDealerOnMap={handleSeeDealerOnMap}
                          selectedDealer={selectedDealer}
                          setSelectedDealer={setSelectedDealer}
                      />
                  ))
                : dealers.map((dealer, index) => (
                      <ListItem
                          settings={props?.settings}
                          key={index}
                          dealer={dealer}
                          loadingDealers={loadingDealers}
                          activeDealer={activeDealer}
                          handleSeeDealerOnMap={handleSeeDealerOnMap}
                          selectedDealer={selectedDealer}
                          setSelectedDealer={setSelectedDealer}
                      />
                  ))}
        </div>
    );
}

export function ListItem({
    dealer,
    loadingDealers: loading,
    activeDealer,
    handleSeeDealerOnMap,
    selectedDealer,
    setSelectedDealer,
    ...props
}: FindDealerListItemProps) {
    const r = (value: string = null) => {
        if (loading) {
            return (
                <SkeletonLoading
                    component="span"
                    width="50%"
                    height="0.8rem"
                    style={{ display: "inline-block", marginBottom: "0.4rem" }}
                />
            );
        }

        return value;
    };

    const isSelected = dealer && dealer?.id === selectedDealer;

    return (
        <div ref={dealer?.element} className="tmp-find-dealer__list__item__container">
            <div
                className={clsx("tmp-find-dealer__list__item", {
                    "tmp-active": dealer?.id === activeDealer,
                    "tmp-selected": isSelected,
                })}
                onMouseEnter={
                    !dealer
                        ? undefined
                        : () => {
                              if (window.matchMedia("(min-width: 700px)").matches) {
                                  handleSeeDealerOnMap(dealer);
                              }
                          }
                }
            >
                <h4>
                    {r(dealer?.address?.city)}
                    {isSelected && (
                        <span className="tmp-find-dealer__name__selected">
                            {props.settings?.find_dealer_selected_text}
                        </span>
                    )}
                </h4>
                {loading ? (
                    <div className="tmp-find-dealer__icons">
                        {[...new Array(3)].map((value, index) => (
                            <SkeletonLoading
                                key={index}
                                style={{ display: "inline-block", marginRight: "0.4rem" }}
                                width="1.6rem"
                                height="1.6rem"
                            />
                        ))}
                    </div>
                ) : (
                    <DealerIcons settings={props.settings} dealer={dealer} />
                )}
                <p>{r(dealer?.name)}</p>
                <p>
                    {r(dealer?.address?.address)}
                    <br />
                    {r((dealer?.address?.zipcode ?? "") + " " + (dealer?.address?.city ?? ""))}
                </p>
                {dealer?.distance && (
                    <p>
                        <span className="tmp-find-dealer__inline-heading">
                            {props.settings?.find_dealer_distance_title}
                        </span>
                        {(dealer.distance / 1000).toFixed(1)}
                        {props.settings?.find_dealer_km_text}
                        <br />
                    </p>
                )}
                <p>
                    {(loading || dealer?.phone) && (
                        <>
                            <span className="tmp-find-dealer__inline-heading">
                                {props?.settings?.find_dealer_phone_title}
                            </span>
                            {loading ? r() : <a href={"tel:" + dealer.phone}>{dealer.phone}</a>}
                            <br />
                        </>
                    )}
                    {(loading || dealer?.email) && (
                        <>
                            <span className="tmp-find-dealer__inline-heading">
                                {props?.settings?.find_dealer_email_title}
                            </span>
                            {loading ? r() : <a href={"mailto:" + dealer.email}>{dealer.email}</a>}
                        </>
                    )}
                </p>
                <p>
                    <a
                        className="tmp-find-dealer__list__item__open-map"
                        onClick={
                            !dealer
                                ? undefined
                                : () => {
                                      handleSeeDealerOnMap(dealer);
                                  }
                        }
                    >
                        {props?.settings?.find_dealer_go_to_map_text}
                    </a>
                </p>
                {setSelectedDealer && (
                    <p>
                        {isSelected ? (
                            <span className="tmp-find-dealer__select">
                                {props.settings?.find_dealer_long_selected_text}
                            </span>
                        ) : (
                            <span
                                className="tmp-find-dealer__select cursor-pointer"
                                onClick={
                                    !dealer
                                        ? undefined
                                        : () => {
                                              setSelectedDealer(dealer.id);
                                          }
                                }
                            >
                                {props?.settings?.find_dealer_select_text}
                            </span>
                        )}
                    </p>
                )}
                <span className="tmp-find-dealer__list__opening-hours-title">
                    {props.settings?.find_dealer_openings_text || "Åbningstider:"}
                </span>
                <br />
                <Openings openings={dealer?.openings} loading={loading} />
                {!!dealer && dealer?.url !== null && !!props.settings.find_dealer_link_text && (
                    <div>
                        <a href={dealer.url}>{props.settings.find_dealer_link_text}</a>
                    </div>
                )}
            </div>
            <div className="tmp-find-dealer__list__divider" />
        </div>
    );
}
